/**
 * SOS & Flag Service
 *
 * Phase 4 - Handles SOS alerts and job flagging.
 *
 * SOS Flow:
 *   1. Tech taps SOS -> reason picker -> confirm
 *   2. Write to sos_alerts/ collection + update job.sos = true
 *   3. job.lastAction = 'sos_triggered' (Zapier signal)
 *   4. All dashboards see real-time alert
 *   5. Office can acknowledge -> sets acknowledgedBy/At
 *   6. Resolved when situation handled
 *
 * Flag Flow:
 *   1. Tech taps Flag -> reason picker -> optional details -> confirm
 *   2. Update job in Firestore (flagged, flagReason, lastAction)
 *   3. Ops team gets notification
 */

import { collection, addDoc, updateDoc, doc, getDoc, arrayUnion } from 'firebase/firestore';
import { db } from '@/firebase';
import type { SOSAlert, FlagAlert, TimeRequestAlert } from '@/types/schema';
import { persistFieldActivity } from '@/services/field-activity.service';

// ---------------------------------------------------------------------------
// Helpers — propagate field alerts to parent deal / fault timelines
// ---------------------------------------------------------------------------

/**
 * Fire-and-forget: write an activity log entry to the parent deal if the job
 * has a linked dealId.
 */
async function propagateToDeal(
  jobId: string,
  alertType: 'SOS' | 'Flagged' | 'Request More Time',
  techName: string,
  reason: string,
  extraMinutes?: number,
): Promise<void> {
  const jobSnap = await getDoc(doc(db, 'jobs', jobId));
  if (!jobSnap.exists()) return;
  const jobData = jobSnap.data();
  const dealId = jobData?.dealId;
  if (!dealId) return;

  // Verify deal still exists before writing
  const dealSnap = await getDoc(doc(db, 'deals', dealId));
  if (!dealSnap.exists()) return;

  const now = new Date().toISOString();
  const extraLabel = extraMinutes ? ` (+${extraMinutes >= 60 ? `${extraMinutes / 60}h` : `${extraMinutes}m`})` : '';
  const entry: Record<string, unknown> = {
    id: crypto.randomUUID(),
    timestamp: now,
    actor: 'system',
    actorName: techName,
    action: `${alertType} — ${jobData.type || 'Job'} at ${jobData.client || 'customer'}${extraLabel}`,
    details: reason,
    automated: true,
  };

  await updateDoc(doc(db, 'deals', dealId), {
    activityLog: arrayUnion(entry),
    updatedAt: now,
  });
}

/**
 * Fire-and-forget: write an investigation history entry to the parent fault
 * if the job has a linked faultId.
 */
async function propagateToFault(
  jobId: string,
  alertType: 'SOS' | 'Flagged' | 'Request More Time',
  techName: string,
  reason: string,
  extraMinutes?: number,
): Promise<void> {
  const jobSnap = await getDoc(doc(db, 'jobs', jobId));
  if (!jobSnap.exists()) return;
  const jobData = jobSnap.data();
  const faultId = jobData?.faultId;
  if (!faultId) return;

  const faultSnap = await getDoc(doc(db, 'faults', faultId));
  if (!faultSnap.exists()) return;
  const faultData = faultSnap.data();

  const now = new Date().toISOString();
  const extraLabel = extraMinutes ? ` (+${extraMinutes >= 60 ? `${extraMinutes / 60}h` : `${extraMinutes}m`})` : '';
  const newEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: faultData?.stage || '03-Deploy',
    author: techName,
    action: `${alertType} triggered on deployment job${extraLabel}`,
    details: reason,
  };

  await updateDoc(doc(db, 'faults', faultId), {
    investigationHistory: arrayUnion(newEntry),
    updatedAt: now,
  });
}

// Helper: today's date as YYYY-MM-DD (local timezone)
function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ============================================================================
// SOS REASONS
// ============================================================================

export const SOS_REASONS = [
  'Need second person',
  'Safety concern',
  'Equipment failure',
  'Customer issue',
  'Other',
] as const;

export type SOSReason = (typeof SOS_REASONS)[number];

// ============================================================================
// FLAG REASONS
// ============================================================================

export const FLAG_REASONS = [
  'Not enough time allocated',
  'Need specific tool/equipment',
  'Need specific vehicle',
  'Need another person',
  'Wrong address',
  'Customer not home / no access',
  'Other',
] as const;

export type FlagReason = (typeof FLAG_REASONS)[number];

// ============================================================================
// SOS OPERATIONS
// ============================================================================

/**
 * Trigger an SOS alert.
 * Creates a document in sos_alerts/ and updates the job.
 */
export async function triggerSOS(params: {
  jobId: string;
  techId: string;
  techName: string;
  reason: SOSReason;
  customReason?: string;
  customerName: string;
  address: string;
  tenantId: string;
}): Promise<string> {
  const now = new Date().toISOString();

  // 1. Create SOS alert document (omit customReason when absent — Firestore rejects undefined)
  //    resolvedAt: null is required — Firestore where('resolvedAt', '==', null) won't match absent fields
  const reasonText = params.reason === 'Other' && params.customReason
    ? `Other: ${params.customReason}`
    : params.reason;

  const alertData: Omit<SOSAlert, 'id'> = {
    jobId: params.jobId,
    techId: params.techId,
    techName: params.techName,
    reason: reasonText,
    customerName: params.customerName,
    address: params.address,
    triggeredAt: now,
    acknowledged: false,
    resolvedAt: null,
    tenantId: params.tenantId,
    ...(params.reason === 'Other' && params.customReason ? { customReason: params.customReason } : {}),
  };

  const alertRef = await addDoc(collection(db, 'sos_alerts'), alertData);

  // 2. Update the job with SOS info
  await updateDoc(doc(db, 'jobs', params.jobId), {
    sos: true,
    sosReason: reasonText,
    sosTime: now,
    sosBy: params.techId,
    sosByName: params.techName,
    lastAction: 'sos_triggered',
    updatedAt: now,
  });

  // 3. Persist to field_activity_log (fire-and-forget — guarantees history even if dashboard is closed)
  const today = toLocalDateStr(new Date());
  persistFieldActivity({
    id: `${today}_sos-${alertRef.id}`,
    eventId: `sos-${alertRef.id}`,
    type: 'sos',
    title: `SOS: ${params.techName}`,
    subtitle: reasonText,
    timestamp: now,
    severity: 'red',
    date: today,
    jobId: params.jobId,
    techId: params.techId,
    techName: params.techName,
    customerName: params.customerName,
    address: params.address,
    tenantId: params.tenantId,
  }).catch(console.error);

  // 4. Propagate to deal/fault timelines (fire-and-forget)
  propagateToDeal(params.jobId, 'SOS', params.techName, reasonText).catch(console.error);
  propagateToFault(params.jobId, 'SOS', params.techName, reasonText).catch(console.error);

  return alertRef.id;
}

/**
 * Acknowledge an SOS alert (office staff action).
 * Also updates the job document so the tech can see the acknowledgement.
 */
export async function acknowledgeSOS(
  alertId: string,
  acknowledgedBy: string,
  jobId: string,
): Promise<void> {
  const now = new Date().toISOString();

  await updateDoc(doc(db, 'sos_alerts', alertId), {
    acknowledged: true,
    acknowledgedBy,
    acknowledgedAt: now,
  });

  // Update the job so the tech's real-time listener picks up the acknowledgement
  await updateDoc(doc(db, 'jobs', jobId), {
    sosAcknowledged: true,
    sosAcknowledgedBy: acknowledgedBy,
    sosAcknowledgedAt: now,
    lastAction: 'sos_acknowledged',
    updatedAt: now,
  });
}

/**
 * Resolve an SOS alert (situation handled).
 * Also clears the SOS flag on the job.
 */
export async function resolveSOS(
  alertId: string,
  jobId: string,
): Promise<void> {
  const now = new Date().toISOString();

  await updateDoc(doc(db, 'sos_alerts', alertId), {
    resolvedAt: now,
  });

  await updateDoc(doc(db, 'jobs', jobId), {
    sos: false,
    sosAcknowledged: false,
    lastAction: 'sos_resolved',
    updatedAt: now,
  });
}

// ============================================================================
// FLAG OPERATIONS
// ============================================================================

/**
 * Flag a job for ops attention.
 * Creates a document in flag_alerts/ and updates the job.
 */
export async function flagJob(params: {
  jobId: string;
  techId: string;
  techName: string;
  reason: FlagReason;
  customReason?: string;
  customerName: string;
  address: string;
  tenantId: string;
}): Promise<string> {
  const now = new Date().toISOString();

  const reasonText = params.reason === 'Other' && params.customReason
    ? `Other: ${params.customReason}`
    : params.reason;

  // 1. Create flag alert document for real-time listener (omit customReason when absent — Firestore rejects undefined)
  //    dismissedAt: null is required — Firestore where('dismissedAt', '==', null) won't match absent fields
  const flagAlertData: Omit<FlagAlert, 'id'> = {
    jobId: params.jobId,
    techId: params.techId,
    techName: params.techName,
    reason: reasonText,
    customerName: params.customerName,
    address: params.address,
    flaggedAt: now,
    acknowledged: false,
    dismissedAt: null,
    tenantId: params.tenantId,
    ...(params.reason === 'Other' && params.customReason ? { customReason: params.customReason } : {}),
  };

  const alertRef = await addDoc(collection(db, 'flag_alerts'), flagAlertData);

  // 2. Update the job
  await updateDoc(doc(db, 'jobs', params.jobId), {
    flagged: true,
    flagReason: reasonText,
    flaggedAt: now,
    flaggedBy: params.techId,
    flaggedByName: params.techName,
    lastAction: 'job_flagged',
    updatedAt: now,
  });

  // 3. Persist to field_activity_log (fire-and-forget)
  const today = toLocalDateStr(new Date());
  persistFieldActivity({
    id: `${today}_flag-${alertRef.id}`,
    eventId: `flag-${params.jobId}`,
    type: 'flagged',
    title: `Flagged: ${params.techName}`,
    subtitle: reasonText,
    timestamp: now,
    severity: 'amber',
    date: today,
    jobId: params.jobId,
    techId: params.techId,
    techName: params.techName,
    customerName: params.customerName,
    address: params.address,
    tenantId: params.tenantId,
  }).catch(console.error);

  // 4. Propagate to deal/fault timelines (fire-and-forget)
  propagateToDeal(params.jobId, 'Flagged', params.techName, reasonText).catch(console.error);
  propagateToFault(params.jobId, 'Flagged', params.techName, reasonText).catch(console.error);

  return alertRef.id;
}

/**
 * Acknowledge a flag alert (ops action).
 */
export async function acknowledgeFlag(
  alertId: string,
  acknowledgedBy: string,
  jobId: string,
): Promise<void> {
  const now = new Date().toISOString();

  await updateDoc(doc(db, 'flag_alerts', alertId), {
    acknowledged: true,
    acknowledgedBy,
    acknowledgedAt: now,
  });

  await updateDoc(doc(db, 'jobs', jobId), {
    flagAcknowledged: true,
    flagAcknowledgedBy: acknowledgedBy,
    flagAcknowledgedAt: now,
    lastAction: 'flag_acknowledged',
    updatedAt: now,
  });
}

// ============================================================================
// REQUEST MORE TIME REASONS
// ============================================================================

export const REQUEST_MORE_TIME_REASONS = [
  'Job more complex than expected',
  'Unexpected issues found',
  'Waiting on customer decision',
  'Additional scope discovered',
  'Equipment troubleshooting',
  'Other',
] as const;

export type RequestMoreTimeReason = (typeof REQUEST_MORE_TIME_REASONS)[number];

// ============================================================================
// REQUEST MORE TIME OPERATIONS
// ============================================================================

/**
 * Request more time on a job.
 * Updates the job document and creates a field activity signal.
 */
export async function requestMoreTime(params: {
  jobId: string;
  techId: string;
  techName: string;
  reason: RequestMoreTimeReason;
  customReason?: string;
  extraMinutes: number;
  customerName: string;
  address: string;
  tenantId: string;
}): Promise<string> {
  const now = new Date().toISOString();

  const reasonText = params.reason === 'Other' && params.customReason
    ? `Other: ${params.customReason}`
    : params.reason;

  // 1. Create time_request_alerts document for real-time office listener (omit customReason when absent — Firestore rejects undefined)
  const timeAlertData: Omit<TimeRequestAlert, 'id'> = {
    jobId: params.jobId,
    techId: params.techId,
    techName: params.techName,
    reason: reasonText,
    extraMinutes: params.extraMinutes,
    customerName: params.customerName,
    address: params.address,
    requestedAt: now,
    acknowledged: false,
    clearedAt: null,
    tenantId: params.tenantId,
    ...(params.reason === 'Other' && params.customReason ? { customReason: params.customReason } : {}),
  };

  const alertRef = await addDoc(collection(db, 'time_request_alerts'), timeAlertData);

  // 2. Update the job with request info
  await updateDoc(doc(db, 'jobs', params.jobId), {
    requestMoreTime: true,
    requestMoreTimeReason: reasonText,
    requestMoreTimeMinutes: params.extraMinutes,
    requestMoreTimeAt: now,
    requestMoreTimeBy: params.techId,
    requestMoreTimeByName: params.techName,
    lastAction: 'request_more_time',
    updatedAt: now,
  });

  // 3. Persist to field_activity_log (fire-and-forget)
  const today = toLocalDateStr(new Date());
  const extraLabel = params.extraMinutes >= 60
    ? `${params.extraMinutes / 60}h`
    : `${params.extraMinutes}m`;
  persistFieldActivity({
    id: `${today}_rmt-${alertRef.id}`,
    eventId: `rmt-${alertRef.id}`,
    type: 'request_more_time',
    title: `More Time: ${params.techName} (+${extraLabel})`,
    subtitle: reasonText,
    timestamp: now,
    severity: 'orange',
    date: today,
    jobId: params.jobId,
    techId: params.techId,
    techName: params.techName,
    customerName: params.customerName,
    address: params.address,
    tenantId: params.tenantId,
  }).catch(console.error);

  // 4. Propagate to deal/fault timelines (fire-and-forget)
  propagateToDeal(params.jobId, 'Request More Time', params.techName, reasonText, params.extraMinutes).catch(console.error);
  propagateToFault(params.jobId, 'Request More Time', params.techName, reasonText, params.extraMinutes).catch(console.error);

  return alertRef.id;
}

/**
 * Acknowledge a request for more time (ops action).
 */
export async function acknowledgeRequestMoreTime(
  jobId: string,
  acknowledgedBy: string,
  alertId?: string,
): Promise<void> {
  const now = new Date().toISOString();

  if (alertId) {
    await updateDoc(doc(db, 'time_request_alerts', alertId), {
      acknowledged: true,
      acknowledgedBy,
      acknowledgedAt: now,
    });
  }

  await updateDoc(doc(db, 'jobs', jobId), {
    requestMoreTimeAcknowledged: true,
    requestMoreTimeAcknowledgedBy: acknowledgedBy,
    requestMoreTimeAcknowledgedAt: now,
    lastAction: 'request_more_time_acknowledged',
    updatedAt: now,
  });
}

/**
 * Clear a request for more time (ops action — after rescheduling is handled).
 */
export async function clearRequestMoreTime(
  jobId: string,
  alertId?: string,
): Promise<void> {
  const now = new Date().toISOString();

  if (alertId) {
    await updateDoc(doc(db, 'time_request_alerts', alertId), {
      clearedAt: now,
    });
  }

  await updateDoc(doc(db, 'jobs', jobId), {
    requestMoreTime: false,
    requestMoreTimeAcknowledged: false,
    lastAction: 'request_more_time_cleared',
    updatedAt: now,
  });
}

// ============================================================================
// FLAG DISMISS
// ============================================================================

/**
 * Dismiss a job flag (ops action).
 * Also marks the flag_alerts document as dismissed.
 */
export async function dismissFlag(
  jobId: string,
  alertId?: string,
): Promise<void> {
  const now = new Date().toISOString();

  if (alertId) {
    await updateDoc(doc(db, 'flag_alerts', alertId), {
      dismissedAt: now,
    });
  }

  await updateDoc(doc(db, 'jobs', jobId), {
    flagged: false,
    flagReason: null,
    flagAcknowledged: false,
    lastAction: 'flag_dismissed',
    updatedAt: now,
  });
}
